import React, { useState, useEffect } from 'react';
import { auth } from '../firebase.config';
import { verifyMpin, isMpinSet, clearMpin, clearSession } from '../utils/mpinManager';

const MpinRelogin = ({ onSuccess, onCancel }) => {
  const [mpin, setMpin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);

  const userId = auth.currentUser?.uid;

  useEffect(() => {
    // Check if MPIN is set for this user
    if (!userId || !isMpinSet(userId)) {
      onSuccess(); // No MPIN set, proceed normally
      return;
    }
  }, [userId, onSuccess]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (isLocked) {
      setError('Too many failed attempts. Please try again later.');
      return;
    }

    if (mpin.length !== 4) {
      setError('MPIN must be exactly 4 digits');
      return;
    }

    if (!/^\d{4}$/.test(mpin)) {
      setError('MPIN must contain only numbers');
      return;
    }

    // Verify MPIN
    if (verifyMpin(userId, mpin)) {
      // MPIN verified successfully
      setMpin('');
      setAttempts(0);
      onSuccess();
    } else {
      // Wrong MPIN
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setMpin('');
      
      if (newAttempts >= 3) {
        setIsLocked(true);
        setError('Too many failed attempts. Please try again later.');
        // Lock for 5 minutes
        setTimeout(() => {
          setIsLocked(false);
          setAttempts(0);
          setError('');
        }, 5 * 60 * 1000);
      } else {
        setError(`Incorrect MPIN. ${3 - newAttempts} attempts remaining.`);
      }
    }
  };

  const handleLogout = () => {
    clearMpin(userId);
    clearSession();
    auth.signOut();
    onCancel();
  };

  if (!userId || !isMpinSet(userId)) {
    return null; // No MPIN set, don't show this component
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 max-h-[92dvh] overflow-y-auto">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Enter MPIN to Continue</h3>
          <p className="text-sm text-gray-600 mb-4">
            Please enter your 4-digit MPIN to access your account.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={mpin}
                onChange={(e) => setMpin(e.target.value)}
                maxLength={4}
                className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••"
                autoFocus
                disabled={isLocked}
              />
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleLogout}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Logout
              </button>
              <button
                type="submit"
                disabled={isLocked || mpin.length !== 4}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Continue
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MpinRelogin;
